/**
 * Guest range calendar, one month at a time, Sunday first.
 *
 * Look and tap rules follow the Flutter `RangeCalendar` (table_calendar):
 * today in a brand ring, range endpoints in filled brand circles, days inside
 * the range tinted, fully blocked days struck through and not tappable, partly
 * blocked days struck through with a warning dot but still tappable. Days are
 * `yyyy-MM-dd` keys so callers decide the zone.
 */
import { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { DateTime } from 'luxon';
import { isNightsDayDisabled, nextNightsOnTap } from '../../helpers/stayNights';

export interface RangeValue {
  start: string | null;
  end: string | null;
}

export interface RangeCalendarProps {
  value: RangeValue;
  onChange: (next: RangeValue) => void;
  /** First selectable day; earlier days are hidden. */
  minKey: string;
  /** Last selectable day; later days are hidden. */
  maxKey: string;
  todayKey: string;
  blocked?: Set<string>;
  partial?: Set<string>;
  /** Pickup is fixed (modify trip, swap): taps only move the end. */
  lockedStart?: boolean;
  /** Only the start is shown even when an end exists (after a rejected pair). */
  showRangeEndUnselected?: boolean;
  /** Bumping this value snaps the view back to the start day. */
  resetNonce?: number;
  /** `single`: every tap selects one day (start = end). `nights`: stays (spec 016), see `helpers/stayNights`. */
  mode?: 'range' | 'single' | 'nights';
  /** `nights` mode: shortest and longest stay. */
  minNights?: number | null;
  maxNights?: number | null;
  /** Spec 028: two months side by side (one navigation for both). */
  monthsShown?: 1 | 2;
  /**
   * Spec 028: with a start and no end, the days under this many days after the start cannot
   * end the trip (greyed out, "Under N days").
   */
  minRangeDays?: number;
  /** Spec 028: the end drawn as an outlined ring (it follows the start, not a tap). */
  endStyle?: 'filled' | 'outline';
  /** Spec 028: a range painted instead of `value` while the pointer hovers a start day. */
  preview?: RangeValue | null;
  /** Spec 028: `bad` paints the range red (it crosses a booked day). */
  tone?: 'normal' | 'bad';
  /** Spec 028: pointer enters or leaves a selectable day. */
  onHoverDay?: (key: string | null) => void;
  className?: string;
}

const Root = styled.div<{ $months: 1 | 2 }>`
  width: 100%;
  user-select: none;
  display: grid;
  grid-template-columns: ${({ $months }) =>
    $months === 2 ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)'};
  gap: 20px;
`;

const Nav = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
`;

const MonthTitle = styled.div`
  flex: 1 1 auto;
  text-align: center;
  font-family: var(--font-display);
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-primary);
`;

const NavButton = styled.button`
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-md);
  color: var(--color-text-primary);
  transition: background-color var(--transition-normal);
  &:hover:not(:disabled) {
    background: var(--color-bg-secondary);
  }
  &:disabled {
    opacity: 0.3;
    cursor: default;
  }
  svg {
    width: 16px;
    height: 16px;
  }
`;

const Weekdays = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  margin-bottom: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-text-tertiary);
  text-align: center;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  row-gap: 2px;
`;

const CELL = 40;

const Blank = styled.div`
  height: ${CELL}px;
`;

type Fill = 'none' | 'full' | 'left' | 'right';

const Cell = styled.button<{
  $start?: boolean;
  $end?: boolean;
  $within?: boolean;
  $fill: Fill;
  $today?: boolean;
  $blocked?: boolean;
  $partial?: boolean;
  $outline?: boolean;
  $bad?: boolean;
  $preview?: boolean;
  $short?: boolean;
}>`
  position: relative;
  height: ${CELL}px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: var(--color-text-primary);
  background: transparent;

  /* Range fill behind the day disc; it spans the whole cell so neighbours join. */
  ${({ $fill }) =>
    $fill !== 'none' &&
    css`
      &::before {
        content: '';
        position: absolute;
        top: 4px;
        bottom: 4px;
        left: ${$fill === 'right' ? '50%' : '0'};
        right: ${$fill === 'left' ? '50%' : '0'};
        background: rgb(var(--color-black-rgb) / 0.14);
      }
    `}

  ${({ $bad, $fill }) =>
    $bad &&
    $fill !== 'none' &&
    css`
      &::before {
        background: rgb(220 38 38 / 0.14);
      }
    `}

  ${({ $preview }) =>
    $preview &&
    css`
      &::before {
        opacity: 0.55;
      }
    `}

  ${({ $within }) =>
    $within &&
    css`
      color: var(--color-black);
      font-weight: 500;
    `}

  > span {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 999px;
    line-height: 1;
  }

  ${({ $today }) =>
    $today &&
    css`
      > span {
        box-shadow: inset 0 0 0 1.5px var(--color-black);
        color: var(--color-black);
        font-weight: 600;
      }
    `}

  ${({ $start, $end }) =>
    ($start || $end) &&
    css`
      > span {
        background: var(--color-black);
        color: var(--color-on-primary);
        font-weight: 600;
        box-shadow: none;
      }
    `}

  /* Spec 028: an end that follows the start is a ring, so it reads as derived. */
  ${({ $end, $outline, $bad }) =>
    $end &&
    $outline &&
    css`
      > span {
        background: transparent;
        color: ${$bad ? '#dc2626' : 'var(--color-black)'};
        box-shadow: inset 0 0 0 2px ${$bad ? '#dc2626' : 'var(--color-black)'};
      }
    `}

  ${({ $short }) =>
    $short &&
    css`
      color: var(--color-text-tertiary);
      cursor: default;
    `}

  ${({ $blocked, $partial }) =>
    ($blocked || $partial) &&
    css`
      > span {
        text-decoration: line-through;
        text-decoration-color: currentColor;
      }
    `}

  ${({ $blocked }) =>
    $blocked &&
    css`
      color: var(--color-text-tertiary);
      cursor: default;
    `}

  ${({ $partial }) =>
    $partial &&
    css`
      &::after {
        content: '';
        position: absolute;
        bottom: 2px;
        left: 50%;
        width: 5px;
        height: 5px;
        margin-left: -2.5px;
        border-radius: 999px;
        background: var(--color-warning);
      }
    `}

  &:hover:not(:disabled) > span {
    background: ${({ $start, $end, $outline }) =>
      $start || ($end && !$outline) ? 'var(--color-black)' : 'var(--color-bg-secondary)'};
  }
  &:focus-visible {
    outline: none;
    > span {
      box-shadow: 0 0 0 2px var(--color-black);
    }
  }
`;

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const Chevron = ({ dir }: { dir: 'left' | 'right' }) => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
    {dir === 'left' ? (
      <path d="M10 3 5 8l5 5" strokeLinecap="round" strokeLinejoin="round" />
    ) : (
      <path d="m6 3 5 5-5 5" strokeLinecap="round" strokeLinejoin="round" />
    )}
  </svg>
);

const monthOf = (key: string | null | undefined, fallback: DateTime): DateTime => {
  const parsed = key ? DateTime.fromISO(key) : null;
  return parsed && parsed.isValid ? parsed.startOf('month') : fallback;
};

/** Flutter `_onDaySelected`: same day twice is a one-day rental; earlier day becomes the start. */
export function nextRangeOnTap(value: RangeValue, day: string, lockedStart = false): RangeValue {
  const { start, end } = value;
  if (lockedStart && start) {
    return { start, end: day < start ? start : day };
  }
  if (!start || (start && end)) {
    return { start: day, end: null };
  }
  if (day === start) return { start, end: start };
  if (day > start) return { start, end: day };
  return { start: day, end: start };
}

export const RangeCalendar = ({
  value,
  onChange,
  minKey,
  maxKey,
  todayKey,
  blocked,
  partial,
  lockedStart = false,
  showRangeEndUnselected = false,
  resetNonce = 0,
  mode = 'range',
  minNights,
  maxNights,
  monthsShown = 1,
  minRangeDays,
  endStyle = 'filled',
  preview = null,
  tone = 'normal',
  onHoverDay,
  className,
}: RangeCalendarProps) => {
  const nightsRules = { blocked: blocked ?? new Set<string>(), minNights, maxNights };
  const minMonth = monthOf(minKey, DateTime.fromISO(todayKey).startOf('month'));
  const maxMonth = monthOf(maxKey, minMonth.plus({ years: 1 }));
  const [viewMonth, setViewMonth] = useState<DateTime>(() =>
    monthOf(value.start ?? todayKey, minMonth),
  );

  // Snap back to the start day after the parent rejects a pair.
  useEffect(() => {
    setViewMonth(monthOf(value.start ?? todayKey, minMonth));
  }, [resetNonce]);

  const lastShown = viewMonth.plus({ months: monthsShown - 1 });
  const canGoBack = viewMonth > minMonth;
  const canGoForward = lastShown < maxMonth;

  // The painted range: the hover preview while there is one, else the value.
  const painted = preview ?? value;
  const start = painted.start;
  const end = showRangeEndUnselected && !preview ? null : painted.end;
  const isPreview = preview !== null;
  // Spec 028: with a start and no end, days too close to it cannot end the trip.
  const shortUntil =
    mode === 'range' && minRangeDays && value.start && !value.end
      ? DateTime.fromISO(value.start).plus({ days: minRangeDays }).toISODate()
      : null;

  const renderMonth = (month: DateTime, index: number) => {
    const first = month.startOf('month');
    const last = month.endOf('month').startOf('day');
    const cells: Array<{ key: string; day: number } | null> = [];
    for (let i = 0; i < first.weekday % 7; i++) cells.push(null);
    for (let d = first; d <= last; d = d.plus({ days: 1 })) {
      const key = d.toISODate() || '';
      cells.push(key >= minKey && key <= maxKey ? { key, day: d.day } : null);
    }
    const showPrev = index === 0;
    const showNext = index === monthsShown - 1;
    return (
      <div key={month.toISODate() ?? String(index)} data-testid="range-calendar-month">
        <Nav>
          <NavButton
            type="button"
            aria-label="Previous month"
            disabled={!canGoBack}
            style={showPrev ? undefined : { visibility: 'hidden' }}
            onClick={() => setViewMonth(m => m.minus({ months: 1 }))}
          >
            <Chevron dir="left" />
          </NavButton>
          <MonthTitle aria-live="polite">{month.toFormat('LLLL yyyy')}</MonthTitle>
          <NavButton
            type="button"
            aria-label="Next month"
            disabled={!canGoForward}
            style={showNext ? undefined : { visibility: 'hidden' }}
            onClick={() => setViewMonth(m => m.plus({ months: 1 }))}
          >
            <Chevron dir="right" />
          </NavButton>
        </Nav>
        <Weekdays aria-hidden="true">
          {WEEKDAYS.map(label => (
            <span key={label}>{label}</span>
          ))}
        </Weekdays>
        <Grid role="grid" aria-label={month.toFormat('LLLL yyyy')}>
          {cells.map((cell, cellIndex) => {
            if (!cell) return <Blank key={`blank-${cellIndex}`} aria-hidden="true" />;
            const { key, day } = cell;
            // Nights mode: a blocked night can still be the checkout, and days that cannot end
            // the stay being picked are greyed out.
            const isBlocked =
              mode === 'nights'
                ? isNightsDayDisabled(key, { start: value.start, end: value.end }, nightsRules)
                : (blocked?.has(key) ?? false);
            const isPartial = !isBlocked && (partial?.has(key) ?? false);
            const beforeLock = lockedStart && value.start !== null && key < value.start;
            const isShort = shortUntil !== null && key > (value.start ?? '') && key < shortUntil;
            const disabled = isBlocked || beforeLock || isShort;
            const isStart = start === key;
            const isEnd = end === key && end !== start;
            const within = start !== null && end !== null && key > start && key < end;
            const hasRange = start !== null && end !== null && start !== end;
            const fill: Fill = within
              ? 'full'
              : hasRange && isStart
                ? 'right'
                : hasRange && isEnd
                  ? 'left'
                  : 'none';
            const inRange = isStart || isEnd || within;
            return (
              <Cell
                key={key}
                type="button"
                role="gridcell"
                data-date={key}
                aria-label={`${DateTime.fromISO(key).toFormat('cccc, LLLL d')}${
                  isShort ? `, under ${minRangeDays} days` : ''
                }`}
                aria-pressed={isStart || isEnd}
                aria-disabled={disabled}
                disabled={disabled}
                title={isShort ? `Under ${minRangeDays} days` : undefined}
                $start={isStart}
                $end={isEnd}
                $within={within}
                $fill={fill}
                $today={key === todayKey}
                $blocked={isBlocked}
                $partial={isPartial}
                $outline={endStyle === 'outline'}
                $bad={tone === 'bad' && inRange}
                $preview={isPreview && inRange}
                $short={isShort}
                onMouseEnter={onHoverDay ? () => onHoverDay(key) : undefined}
                onMouseLeave={onHoverDay ? () => onHoverDay(null) : undefined}
                onClick={() =>
                  onChange(
                    mode === 'single'
                      ? { start: key, end: key }
                      : mode === 'nights'
                        ? nextNightsOnTap({ start: value.start, end: value.end }, key, nightsRules)
                        : nextRangeOnTap({ start: value.start, end: value.end }, key, lockedStart),
                  )
                }
              >
                <span>{day}</span>
              </Cell>
            );
          })}
        </Grid>
      </div>
    );
  };

  return (
    <Root className={className} data-testid="range-calendar" $months={monthsShown}>
      {Array.from({ length: monthsShown }, (_, index) =>
        renderMonth(viewMonth.plus({ months: index }), index),
      )}
    </Root>
  );
};

export default RangeCalendar;
